// src/components/MultiSearchSelect.jsx
// Search box with database-backed type-ahead and multi-select.
// Typing fetches distinct matching values from /portal/suggest and
// lists them with checkboxes; picked values become removable chips.
// Free text still works — Enter adds whatever is typed as a term.
// The box stays one line and the same width however many are picked
// (3.9.1): the first pick shows as a chip, the rest as "+N more"; the open
// list starts with everything picked, each removable.
// 3.14.0: picking a suggestion keeps what was typed and its matching list
// open, so several matches ("Prabha…") can be picked in a row; the typed
// text clears when the box is left. Up to 50 suggestions, scrolling.
import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { fetchSuggestions } from '../utils/api';
import Emo from './Emo';

// Typed digits find a number however it's written: "6172467" matches
// "+1 (617) 246-7168" (3.25.2)
function digitsMatch(needle, option) {
  const d = needle.replace(/\D/g, '');
  return d.length >= 3 && /^[\d\s()+.-]+$/.test(needle) && String(option).replace(/\D/g, '').includes(d);
}

// options (3.16.0): a list of values to suggest from instead of the server
// (e.g. what the Activity log has loaded)
export default function MultiSearchSelect({ suggestType, field, values, onChange, placeholder, options: localOptions = null }) {
  const { getToken }            = useAuth();
  const [text, setText]         = useState('');
  const [options, setOptions]   = useState([]);
  const [open, setOpen]         = useState(false);
  const [loading, setLoading]   = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const wrapRef     = useRef(null);
  const inputRef    = useRef(null);
  const debounceRef = useRef(null);
  const fetchSeqRef = useRef(0);
  // A suggestion was picked since the text was typed: the text was only for
  // finding matches, so leaving the box drops it instead of adding it
  const pickedRef   = useRef(false);

  // Close on outside click
  useEffect(() => {
    function onDocClick(e) {
      if (!wrapRef.current) return;
      // A mid-click re-render can detach the clicked option node before
      // this listener runs — a detached target is not an outside click
      if (!document.contains(e.target)) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const loadOptions = useCallback(async (q) => {
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    try {
      const needle = String(q || '').trim().toLowerCase();
      const data = localOptions
        ? localOptions.filter(o => !needle || String(o).toLowerCase().includes(needle) || digitsMatch(needle, o)).slice(0, 50)
        : await fetchSuggestions(suggestType, field, q, getToken);
      if (seq === fetchSeqRef.current) setOptions(data);
    } catch {
      if (seq === fetchSeqRef.current) setOptions([]);
    }
    if (seq === fetchSeqRef.current) setLoading(false);
  }, [suggestType, field, getToken, localOptions]);

  function openDropdown() {
    setOpen(true);
    setHighlight(-1);
    loadOptions(text);
  }

  function handleTextChange(e) {
    const q = e.target.value;
    pickedRef.current = false;
    setText(q);
    setOpen(true);
    setHighlight(-1);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => loadOptions(q), 300);
  }

  function toggleValue(v) {
    onChange(values.includes(v) ? values.filter(x => x !== v) : [...values, v]);
    // The typed text and its matches stay, for picking more of them
    pickedRef.current = true;
    inputRef.current?.focus();
  }

  function addFreeText() {
    const v = text.trim();
    if (!v) return;
    if (!values.includes(v)) onChange([...values, v]);
    setText('');
    loadOptions('');
  }

  function removeChip(v) {
    onChange(values.filter(x => x !== v));
  }

  // The "Add ..." row appears when the typed text isn't already an
  // exact option or selection
  const trimmed  = text.trim();
  const showAdd  = trimmed && !options.includes(trimmed) && !values.includes(trimmed);
  const rowCount = (showAdd ? 1 : 0) + options.length;

  function handleKeyDown(e) {
    if (e.key === 'Backspace' && !text && values.length) {
      removeChip(values[values.length - 1]);
      return;
    }
    if (e.key === 'Escape') { setOpen(false); return; }
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) { openDropdown(); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, rowCount - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlight < 0) { addFreeText(); return; }
      if (showAdd && highlight === 0) { addFreeText(); return; }
      const opt = options[showAdd ? highlight - 1 : highlight];
      if (opt !== undefined) toggleValue(opt);
    }
  }

  return (
    <div ref={wrapRef} style={CS.wrap}>
      <div style={CS.box} className="cp-field-box" onClick={() => { inputRef.current?.focus(); if (!open) openDropdown(); }}>
        {values.slice(0, 1).map(v => (
          <span key={v} style={CS.chip} title={v}>
            <span style={CS.chipText}>{v}</span>
            <span style={CS.chipX} onClick={e => { e.stopPropagation(); removeChip(v); }} title="Remove">×</span>
          </span>
        ))}
        {values.length > 1 && (
          <span style={CS.more} title={values.slice(1).join(', ')}>+{values.length - 1} more</span>
        )}
        <input
          ref={inputRef}
          type="text"
          value={text}
          placeholder={values.length ? '' : placeholder}
          onChange={handleTextChange}
          onFocus={openDropdown}
          onBlur={() => { if (pickedRef.current) { setText(''); pickedRef.current = false; } else addFreeText(); setOpen(false); }}
          onKeyDown={handleKeyDown}
          style={CS.input}
        />
      </div>

      {open && (
        <div style={CS.dropdown}>
          {values.length > 1 && (
            <div style={CS.pickedBox}>
              <div style={CS.pickedTitle}>Selected ({values.length})</div>
              <div style={CS.pickedList}>
                {values.map(v => (
                  <span key={v} style={CS.chip} title={v}>
                    <span style={CS.chipText}>{v}</span>
                    <span style={CS.chipX} onMouseDown={e => { e.preventDefault(); removeChip(v); }} title="Remove">×</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          {showAdd && (
            <div
              style={{ ...CS.option, ...(highlight === 0 ? CS.optionActive : {}), color: 'var(--t-00695c)', fontWeight: 600 }}
              onMouseEnter={() => setHighlight(0)}
              onMouseDown={e => { e.preventDefault(); addFreeText(); }}>
              <Emo e="➕" /> Search for “{trimmed}”
            </div>
          )}
          {loading ? (
            <div style={CS.note}>Loading…</div>
          ) : options.length === 0 && !showAdd ? (
            <div style={CS.note}>No matches found</div>
          ) : options.map((opt, i) => {
            const idx      = showAdd ? i + 1 : i;
            const selected = values.includes(opt);
            return (
              <div key={opt}
                style={{ ...CS.option, ...(highlight === idx ? CS.optionActive : {}) }}
                onMouseEnter={() => setHighlight(idx)}
                onMouseDown={e => { e.preventDefault(); toggleValue(opt); }}>
                <input type="checkbox" readOnly checked={selected} style={CS.checkbox} />
                <span style={selected ? { fontWeight: 600, color: 'var(--t-00695c)' } : {}}>{opt}</span>
              </div>
            );
          })}
          {values.length > 0 && (
            <div style={CS.footer}>
              {values.length} selected
              <span style={CS.clearAll} onMouseDown={e => { e.preventDefault(); onChange([]); }}>Clear all</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const CS = {
  wrap: { position: 'relative', width: 240, maxWidth: '100%' },
  box: {
    display: 'flex', flexWrap: 'nowrap', gap: 4, alignItems: 'center', overflow: 'hidden',
    padding: '5px 8px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)',
    background: 'var(--c-ffffff)', cursor: 'text', height: 35, boxSizing: 'border-box'
  },
  chip: {
    display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0, flexShrink: 1,
    background: 'var(--c-e0f2f1)', color: 'var(--t-00695c)', borderRadius: 20,
    padding: '2px 8px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap'
  },
  chipText: { overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 120 },
  more: { flexShrink: 0, background: 'var(--c-00695c)', color: 'var(--t-ffffff)', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' },
  pickedBox: { padding: '8px 10px', borderBottom: '1px solid var(--c-f0f0f0)', background: 'var(--c-fafafa)' },
  pickedTitle: { fontSize: 11, fontWeight: 700, color: 'var(--t-546e7a)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 },
  pickedList: { display: 'flex', flexWrap: 'wrap', gap: 4, maxHeight: 96, overflowY: 'auto' },
  chipX: { cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: '0 2px', color: 'var(--t-00897b)' },
  input: {
    flex: 1, minWidth: 40, border: 'none', outline: 'none',
    fontSize: 13, color: 'var(--t-333333)', fontFamily: 'inherit', padding: '3px 2px', background: 'transparent'
  },
  dropdown: {
    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 800,
    marginTop: 4, background: 'var(--c-ffffff)', borderRadius: 8,
    border: '1px solid var(--c-e0e0e0)', boxShadow: '0 6px 20px rgb(var(--c-000000-rgb) / 0.12)',
    maxHeight: 320, overflowY: 'auto', minWidth: 240
  },
  option: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '8px 12px', fontSize: 13, color: 'var(--t-333333)', cursor: 'pointer'
  },
  optionActive: { background: 'var(--c-e0f2f1)' },
  checkbox: { cursor: 'pointer', accentColor: 'var(--c-00897b)', pointerEvents: 'none' },
  note: { padding: '10px 12px', fontSize: 12, color: 'var(--t-90a4ae)' },
  footer: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '6px 12px', fontSize: 11, color: 'var(--t-546e7a)',
    borderTop: '1px solid var(--c-f0f0f0)', background: 'var(--c-fafafa)',
    position: 'sticky', bottom: 0
  },
  clearAll: { cursor: 'pointer', color: 'var(--t-b71c1c)', fontWeight: 600 }
};
